import { z } from 'zod';
import { DateSchema, ObjectIdSchema, mapFromObjectOrMapStrict } from './base';
import { DeviceClasses, TrackerActivationSchema, TrackerStateSchema } from './trackers';

export const TagEntry = z.object({
  value: z.string(),
  label: z.string().optional(),
  count: z.number().optional(),
});

export type TagEntry = z.infer<typeof TagEntry>;

export const TagsCollectionSchema = z
  .union([z.map(z.string(), z.array(TagEntry)), z.record(z.string(), z.array(TagEntry))])
  .transform((v) => {
    if (v instanceof Map) {
      return v;
    }

    return new Map(Object.entries(v).map(([key, value]) => [key, value as Array<TagEntry>]));
  });

export type TagsCollection = z.infer<typeof TagsCollectionSchema>;

export const TagsSchema = z.record(z.string(), z.string());

export type Tags = z.infer<typeof TagsSchema>;

const StarterSchema = z.object({
  id: ObjectIdSchema,
  startNumber: z.string(),
  eventId: ObjectIdSchema,
  name: z.string(),
  trackId: ObjectIdSchema.optional(),
  appId: z.string().nullish(),
  deviceType: z.string().nullable(),
  deviceClass: z.enum(DeviceClasses).nullable(),
  times: mapFromObjectOrMapStrict(z.string(), DateSchema),
  manualFinishDuration: z.number().nullable(),
  key: z.string().optional(),
  startTime: DateSchema.nullish(),
  endTime: DateSchema.nullish(),
  markerColor: z.string().nullable(),
  integrations: z.object({
    importId: z.string().optional(),
    importKey: z.string().optional(),
    importAppId: z.string().nullable(),
    messagingToken: z.string().nullish(),
  }),
  tags: TagsSchema,
  updatedAt: DateSchema,
  createdAt: DateSchema.optional(),
  gpxUrl: z.string().optional(),
  listPosition: z.number().optional(),
  deviceGpxFix: z.boolean().optional(),
  deviceName: z.string().optional(),
  deviceActivation: TrackerActivationSchema.optional(),
  deviceState: TrackerStateSchema.optional(),
  deviceOwnerId: ObjectIdSchema.optional(),
  deviceId: ObjectIdSchema.nullish(),
});

export type Starter = z.infer<typeof StarterSchema>;

export const StartersSchema = z.array(StarterSchema);
export type Starters = z.infer<typeof StartersSchema>;

export const StarterSyncReport = z.object({
  status: z.enum(['skipped', 'success', 'partial', 'error']),
  reason: z.string().optional(),
  errors: z.array(
    z.object({
      errorType: z.string(),
      id: z.string(),
      message: z.string(),
      name: z.string(),
      eventId: z.string().optional(),
      sourceUrl: z.string().optional(),
      record: z.any().optional(),
      importKey: z.string().optional(),
      importId: z.string().optional(),
      starterKey: z.string().optional(),
    }),
  ),
  counts: z
    .object({
      created: z.number(),
      updated: z.number(),
      notChanged: z.number(),
      skipped: z.number(),
      markedAbandoned: z.number(),
      deleted: z.number(),
    })
    .nullable(),
});

export type StarterSyncReport = z.infer<typeof StarterSyncReport>;
